import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Menu, X, User as UserIcon, LogIn, Loader2 } from "lucide-react";
import { useCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";

export const Navbar = () => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { user } = useAuth();
  const isAuthed = user && user !== false;
  const { startCheckout, loadingPlan } = useCheckout();

  const sectionLinks = [
    { label: "Examples", hash: "#examples" },
    { label: "Benefits", hash: "#benefits" },
    { label: "How it works", hash: "#how" },
    { label: "Pricing", hash: "#pricing" },
    { label: "FAQ", hash: "#faq" },
    { label: "Contact", hash: "#contact" },
  ];

  const pageLinks = [
    { label: "Resume Writing", to: "/resume-writing-service" },
    { label: "AI Builder", to: "/ai-resume-builder" },
  ];

  // Section links: same-page hash on home, otherwise "/#hash" so they navigate first.
  const sectionHref = (hash) => (isHome ? hash : `/${hash}`);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 mt-3">
        <div className="glass rounded-full px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <span className="relative inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {pageLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm transition-colors ${
                  location.pathname === l.to ? "text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`nav-page-${l.to.slice(1)}`}
              >
                {l.label}
              </Link>
            ))}
            <span aria-hidden className="h-3.5 w-px bg-white/10" />
            {sectionLinks.slice(0, 4).map((l) => (
              <a
                key={l.hash}
                href={sectionHref(l.hash)}
                className="text-sm text-zinc-400 hover:text-white transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {isAuthed && user.is_admin && (
              <Link
                to="/admin"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/10 px-3.5 py-2 text-sm text-amber-200 hover:bg-amber-500/20 transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
            {isAuthed ? (
              <Link
                to="/account"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-2 text-sm text-white hover:bg-white/10 transition-colors"
                data-testid="nav-account"
                title={user.email}
              >
                <UserIcon className="h-3.5 w-3.5" />
                <span className="max-w-[120px] truncate">
                  {user.name || user.email.split("@")[0]}
                </span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-2 text-sm text-zinc-200 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="nav-login"
              >
                <LogIn className="h-3.5 w-3.5" />
                Sign in
              </Link>
            )}
            <button
              onClick={() => startCheckout("cv_pro")}
              disabled={loadingPlan === "cv_pro"}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
              data-testid="nav-cta-get-resume"
            >
              {loadingPlan === "cv_pro" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Get My Resume
            </button>
            <button
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/10 text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="nav-menu-toggle"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-2 glass rounded-2xl p-4 space-y-1" data-testid="mobile-menu">
            <p className="px-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              Pages
            </p>
            {pageLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block text-sm text-zinc-200 hover:text-white py-2 px-2 rounded-md hover:bg-white/5"
                data-testid={`mobile-page-${l.to.slice(1)}`}
              >
                {l.label}
              </Link>
            ))}
            <div aria-hidden className="my-2 h-px bg-white/5" />
            <p className="px-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              Sections
            </p>
            {sectionLinks.map((l) => (
              <a
                key={l.hash}
                href={sectionHref(l.hash)}
                onClick={() => setOpen(false)}
                className="block text-sm text-zinc-300 hover:text-white py-2 px-2 rounded-md hover:bg-white/5"
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                startCheckout("cv_pro");
              }}
              className="w-full mt-3 inline-flex items-center justify-center rounded-full bg-white text-black px-4 py-2 text-sm font-medium"
              data-testid="mobile-cta-get-resume"
            >
              Get My Resume
            </button>
            {isAuthed ? (
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white"
                data-testid="mobile-account"
              >
                <UserIcon className="h-3.5 w-3.5" />
                {user.name || user.email.split("@")[0]}
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white"
                data-testid="mobile-login"
              >
                <LogIn className="h-3.5 w-3.5" />
                Sign in
              </Link>
            )}
          </div>
        )}
      </div>
    </motion.header>
  );
};
